/**
 * TeamsPage — `/teams` admin page.
 *
 * Permission model:
 *   - READ open to any authenticated user (see the roster).
 *   - WRITE (add / edit / delete member, seed from Notion) gated to the
 *     single admin email. Backend enforces on every POST/PUT/DELETE; the FE
 *     additionally hides write buttons for non-admins and shows a read-only
 *     Alert so they know why the buttons are absent.
 *
 * Layout:
 *   1. Page header + team dev-day chip
 *   2. Read-only banner (non-admins only)
 *   3. Members table  (Name, Email, Role, Lead chip, Dev-days/wk, Slack, Active)
 *   4. Add member + Seed from Notion actions (admin only)
 *   5. MemberDialog (create + edit)
 *   6. Delete confirmation dialog
 *
 * Three-state rule: loading / empty / error are all handled explicitly.
 */

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import { useCallback, useState, type ReactElement } from 'react';

import { isAdminEmail } from '@/auth/admin';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError, teams as teamsApi } from '@/helpers/ApiHelper';
import { useTeamRoster } from '@/hooks/useTeamRoster';
import type { TeamMember } from '@/types/team';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function TeamsPage(): ReactElement {
  const { user } = useAuth();
  const canEdit = isAdminEmail(user?.email);

  const { team, members, isLoading, error, refetch } = useTeamRoster();

  const [memberDialog, setMemberDialog] = useState<MemberDialogState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TeamMember | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [seedError, setSeedError] = useState<string | null>(null);
  const [seedSuccess, setSeedSuccess] = useState<string | null>(null);

  const handleSeed = useCallback(async () => {
    setSeedError(null);
    setSeedSuccess(null);
    setSeeding(true);
    try {
      const result = await teamsApi.seedRoster();
      setSeedSuccess(`Seeded ${result.seeded}, skipped ${result.skipped}.`);
      refetch();
    } catch (e) {
      setSeedError(e instanceof ApiError ? e.detail : 'Seed failed');
    } finally {
      setSeeding(false);
    }
  }, [refetch]);

  const handleOpenAdd = useCallback(() => {
    if (!team) return;
    setMemberDialog({ mode: 'create', teamId: team.id });
  }, [team]);

  const handleOpenEdit = useCallback((member: TeamMember) => {
    if (!team) return;
    setMemberDialog({ mode: 'edit', teamId: team.id, member });
  }, [team]);

  const handleDialogSaved = useCallback(() => {
    setMemberDialog(null);
    refetch();
  }, [refetch]);

  const handleDeleteConfirmed = useCallback(async () => {
    if (!team || !deleteTarget) return;
    // Let the error propagate to DeleteMemberDialog which owns the error state.
    await teamsApi.deleteMember(team.id, deleteTarget.id);
    setDeleteTarget(null);
    refetch();
  }, [team, deleteTarget, refetch]);

  // Loading skeleton
  if (isLoading) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto' }} role="status" aria-label="Loading teams">
        <Skeleton variant="text" width={200} height={48} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={320} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={refetch}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="baseline" spacing={2} sx={{ mb: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            color: 'primary.main',
            textShadow: '0 0 6px rgba(217, 119, 87, 0.55)',
          }}
        >
          teams
        </Typography>
        {team && (
          <Tooltip title="Total dev-days per week across active members">
            <Chip
              label={`${team.total_dev_days_per_week} dev-days/wk`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Tooltip>
        )}
        <Typography variant="body2" color="text.secondary">
          {team ? team.name : 'No team configured'}
        </Typography>
      </Stack>

      {/* Non-admin read-only banner */}
      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }} role="status">
          Read-only view. Add, edit, delete, and seed actions are restricted to
          the admin (mohit@detectinspections.com).
        </Alert>
      )}

      {/* Seed feedback */}
      {seedSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSeedSuccess(null)}>
          {seedSuccess}
        </Alert>
      )}
      {seedError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }} onClose={() => setSeedError(null)}>
          {seedError}
        </Alert>
      )}

      {/* Members table */}
      {members.length === 0 ? (
        <EmptyState canEdit={canEdit} onAdd={handleOpenAdd} />
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Team members">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Lead</TableCell>
                <TableCell align="right">Dev-days/wk</TableCell>
                <TableCell>Slack</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Notify</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  canEdit={canEdit}
                  onEdit={handleOpenEdit}
                  onDelete={setDeleteTarget}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Admin action bar */}
      {canEdit && (
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            disabled={!team}
            sx={{ minHeight: 44 }}
          >
            Add member
          </Button>
          <Button
            variant="outlined"
            startIcon={seeding ? <CircularProgress size={16} /> : <CloudSyncIcon />}
            onClick={() => void handleSeed()}
            disabled={seeding}
            sx={{ minHeight: 44 }}
          >
            Seed from Notion
          </Button>
        </Stack>
      )}

      {/* Member create/edit dialog */}
      {memberDialog && (
        <MemberDialog
          state={memberDialog}
          onClose={() => setMemberDialog(null)}
          onSaved={handleDialogSaved}
        />
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <DeleteMemberDialog
          member={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirmed}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

interface EmptyStateProps {
  canEdit: boolean;
  onAdd: () => void;
}

function EmptyState({ canEdit, onAdd }: EmptyStateProps): ReactElement {
  return (
    <Box
      sx={{
        py: 6,
        textAlign: 'center',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        No team members yet.
      </Typography>
      {canEdit && (
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd} sx={{ minHeight: 44 }}>
            Add member
          </Button>
        </Stack>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Table row
// ---------------------------------------------------------------------------

interface MemberRowProps {
  member: TeamMember;
  canEdit: boolean;
  onEdit: (m: TeamMember) => void;
  onDelete: (m: TeamMember) => void;
}

function MemberRow({ member, canEdit, onEdit, onDelete }: MemberRowProps): ReactElement {
  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {member.name}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{member.email}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {member.role ?? '—'}
        </Typography>
      </TableCell>
      <TableCell>
        {member.is_lead ? (
          <Chip label="lead" size="small" color="primary" variant="filled" />
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{member.dev_days_per_week}</Typography>
      </TableCell>
      <TableCell>
        {member.slack_user_id ? (
          <Chip label="resolved" size="small" color="success" variant="outlined" />
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={member.active ? 'active' : 'inactive'}
          size="small"
          color={member.active ? 'success' : 'default'}
          variant={member.active ? 'filled' : 'outlined'}
        />
      </TableCell>
      <TableCell>
        <Chip
          label={member.notifications_enabled ? 'notify' : 'muted'}
          size="small"
          color={member.notifications_enabled ? 'success' : 'default'}
          variant={member.notifications_enabled ? 'filled' : 'outlined'}
        />
      </TableCell>
      {canEdit && (
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Tooltip title="Edit member">
            <IconButton
              aria-label={`Edit ${member.name}`}
              size="large"
              onClick={() => onEdit(member)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete member">
            <IconButton
              aria-label={`Delete ${member.name}`}
              size="large"
              color="error"
              onClick={() => onDelete(member)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      )}
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Member dialog (create + edit)
// ---------------------------------------------------------------------------

type MemberDialogState =
  | { mode: 'create'; teamId: string }
  | { mode: 'edit'; teamId: string; member: TeamMember };

interface MemberDialogProps {
  state: MemberDialogState;
  onClose: () => void;
  onSaved: () => void;
}

interface MemberFormValues {
  name: string;
  email: string;
  role: string;
  dev_days_per_week: string;
  is_lead: boolean;
  active: boolean;
  notifications_enabled: boolean;
  slack_user_id: string;
  notion_person_id: string;
}

function MemberDialog({ state, onClose, onSaved }: MemberDialogProps): ReactElement {
  const initial: MemberFormValues =
    state.mode === 'edit'
      ? {
          name: state.member.name,
          email: state.member.email,
          role: state.member.role ?? '',
          dev_days_per_week: String(state.member.dev_days_per_week),
          is_lead: state.member.is_lead,
          active: state.member.active,
          notifications_enabled: state.member.notifications_enabled,
          slack_user_id: state.member.slack_user_id ?? '',
          notion_person_id: state.member.notion_person_id ?? '',
        }
      : {
          name: '',
          email: '',
          role: '',
          dev_days_per_week: '5',
          is_lead: false,
          active: true,
          notifications_enabled: true,
          slack_user_id: '',
          notion_person_id: '',
        };

  const [form, setForm] = useState<MemberFormValues>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof MemberFormValues, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    const devDays = parseFloat(form.dev_days_per_week);
    if (isNaN(devDays) || devDays < 0) {
      setError('Dev-days/wk must be a non-negative number.');
      return;
    }
    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required.');
      return;
    }

    const body = {
      name: form.name.trim(),
      email: form.email.trim(),
      role: form.role.trim() || undefined,
      dev_days_per_week: devDays,
      is_lead: form.is_lead,
      active: form.active,
      notifications_enabled: form.notifications_enabled,
      slack_user_id: form.slack_user_id.trim() || undefined,
      notion_person_id: form.notion_person_id.trim() || undefined,
    };

    setBusy(true);
    setError(null);
    try {
      if (state.mode === 'create') {
        await teamsApi.createMember(state.teamId, body);
      } else {
        await teamsApi.updateMember(state.teamId, state.member.id, body);
      }
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{state.mode === 'create' ? 'Add member' : 'Edit member'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            label="Name"
            id="member-name"
            size="small"
            fullWidth
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            required
          />
          <TextField
            label="Email"
            id="member-email"
            size="small"
            fullWidth
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            required
          />
          <TextField
            label="Role"
            id="member-role"
            size="small"
            fullWidth
            value={form.role}
            onChange={(e) => set('role', e.target.value)}
            placeholder="e.g. Senior Engineer"
          />
          <TextField
            label="Dev-days / week"
            id="member-dev-days"
            size="small"
            fullWidth
            type="number"
            inputProps={{ min: 0, max: 7, step: 0.5 }}
            value={form.dev_days_per_week}
            onChange={(e) => set('dev_days_per_week', e.target.value)}
          />
          <TextField
            label="Slack user ID"
            id="member-slack"
            size="small"
            fullWidth
            value={form.slack_user_id}
            onChange={(e) => set('slack_user_id', e.target.value)}
            placeholder="U0XXXXXXXX"
          />
          <TextField
            label="Notion person ID"
            id="member-notion"
            size="small"
            fullWidth
            value={form.notion_person_id}
            onChange={(e) => set('notion_person_id', e.target.value)}
            placeholder="UUID"
          />
          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_lead}
                  onChange={(e) => set('is_lead', e.target.checked)}
                  inputProps={{ 'aria-label': 'Team lead' }}
                />
              }
              label="Team lead"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.active}
                  onChange={(e) => set('active', e.target.checked)}
                  inputProps={{ 'aria-label': 'Active' }}
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.notifications_enabled}
                  onChange={(e) => set('notifications_enabled', e.target.checked)}
                  inputProps={{ 'aria-label': 'Notify' }}
                />
              }
              label="Notify"
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={busy || !form.name.trim() || !form.email.trim()}
        >
          {state.mode === 'create' ? 'Add' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Delete confirmation dialog
// ---------------------------------------------------------------------------

interface DeleteMemberDialogProps {
  member: TeamMember;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

function DeleteMemberDialog({ member, onClose, onConfirm }: DeleteMemberDialogProps): ReactElement {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Remove member</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          )}
          <DialogContentText>
            Remove <strong>{member.name}</strong> ({member.email}) from the roster? This action
            cannot be undone.
          </DialogContentText>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={busy}
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}
